// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createTypedSelector, selectFeature } from '@shared/data-access/utils';
import { type RootTagManagerState } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { groupMap } from '@shared/data-access/utils/groupby';
import {
  type Tag,
  type TickerTagState,
} from '@shared/data-access/interfaces/redux/tag-manager-state';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';

export const tickerTagEntityAdapter = createEntityAdapter<Tag, string>({
  selectId: ({ name }) => name,
  sortComparer: (current, next) => current.name.localeCompare(next.name),
});

export const tagManagerEntityAdapter = createEntityAdapter<TickerTagState, string>({
  selectId: ({ feature }) => feature,
  sortComparer: (current, next) => current.feature.localeCompare(next.feature),
});

export const watchListEntityAdapter = createEntityAdapter<
  WatchListPosition,
  string
>({
  selectId: ({ ticker }) => ticker,
  sortComparer: (current, next) =>
    Date.parse(next.saved_at) - Date.parse(current.saved_at),
});

const tickerTagSelectors = tickerTagEntityAdapter.getSelectors();

const tagManagerSelectors = tagManagerEntityAdapter.getSelectors();

const watchListSelectors = watchListEntityAdapter.getSelectors();

const createSelector = createTypedSelector<RootTagManagerState>();

export const selectTagManagerState = (state: RootTagManagerState) =>
  state.tagManager;

export const selectTagsState = createSelector(
  selectTagManagerState,
  ({ tags }) => tags,
);

export const selectWatchlistState = createSelector(
  selectTagManagerState,
  state => state.watchlist,
);

export const selectTagManagerFeature = createSelector(
  [selectTagsState, (_, feature: TickerTagFeature) => feature],
  (state, feature) => tagManagerSelectors.selectById(state, feature),
);

export const selectSelectTag = createSelector(
  selectTagManagerFeature,
  state => state?.selectedTag,
);

export const selectTags = createSelector(
  selectTagManagerFeature,
  state => state?.tags,
);

export const selectAllTags = createSelector(selectTags, state =>
  tickerTagSelectors.selectAll(state!),
);

export const selectTag = createSelector(
  [
    selectTagsState,
    (_: RootTagManagerState, feature: TickerTagFeature) => feature,
    (_: RootTagManagerState, _f: TickerTagFeature, tag: string) => tag,
  ],
  (state, feature, tag) =>
    tickerTagSelectors.selectById(
      tagManagerSelectors.selectById(state, feature)!.tags,
      tag,
    ),
);

export const selectTagTickers = createSelector(
  [
    selectTagsState,
    (_: RootTagManagerState, feature: TickerTagFeature) => feature,
    (_: RootTagManagerState, _f: TickerTagFeature, tag: string) => tag,
  ],
  (state, feature, tag) =>
    tickerTagSelectors.selectById(
      tagManagerSelectors.selectById(state, feature)!.tags,
      tag,
    ),
);

export const selectTagsNames = createSelector(selectTags, state =>
  tickerTagSelectors
    .selectAll(state!)
    .map(({ name }) => name)
    .filter(tag => !['', 'ALL'].includes(tag)),
);

type TickersForTags = Array<{
  name: string;
  assetClass?: string;
  ticker: string;
  isin?: string | null;
  url_logo?: string;
}>;

export const selectTagsByTicker = createSelector(
  [
    selectTagsState,
    (_: RootTagManagerState, feature: TickerTagFeature) => feature,
    (_: RootTagManagerState, _f: TickerTagFeature, tickers: TickersForTags) =>
      tickers,
  ],
  (state, feature, tickers) => {
    const tags = tickerTagSelectors.selectAll(
      tagManagerSelectors.selectById(state, feature)!.tags,
    );

    return groupMap(
      ({ ticker }) => ticker,
      ({ ticker, name, assetClass, isin, url_logo }) => ({
        ticker,
        name,
        assetClass,
        isin,
        url_logo,
        tags: tags
          .filter(tag => tag.tickers.some(t => t === ticker))
          .map(tag => tag.name),
      }),
      (_, tickerTags) => tickerTags,
      tickers,
    ).flat();
  },
);

export const selectTagSuggestions = createSelector(
  [selectTagsState, (_, feature: TickerTagFeature) => feature],
  (state, feature) => {
    const portfolioTags = tickerTagSelectors
      .selectAll(
        tagManagerSelectors.selectById(state, TickerTagFeature.PortfolioPositions)!
          .tags,
      )
      .map(({ name }) => ({
        tagName: name,
        feature: TickerTagFeature.PortfolioPositions,
      }));

    const watchListTags = tickerTagSelectors
      .selectAll(
        tagManagerSelectors.selectById(state, TickerTagFeature.SelectedAssets)!
          .tags,
      )
      .map(({ name }) => ({
        tagName: name,
        feature: TickerTagFeature.SelectedAssets,
      }));

    const aggregatorTags = tickerTagSelectors
      .selectAll(
        tagManagerSelectors.selectById(state, TickerTagFeature.AggregatorPositions)!
          .tags,
      )
      .map(({ name }) => ({
        tagName: name,
        feature: TickerTagFeature.AggregatorPositions,
      }));

    const suggestions = selectFeature(
      {
        [TickerTagFeature.PortfolioPositions]: [
          ...watchListTags,
          ...aggregatorTags,
        ].filter(tag => !portfolioTags.find(pt => pt.tagName === tag.tagName)),

        [TickerTagFeature.SelectedAssets]: [
          ...portfolioTags,
          ...aggregatorTags,
        ].filter(tag => !watchListTags.find(wt => wt.tagName === tag.tagName)),

        [TickerTagFeature.AggregatorPositions]: [
          ...portfolioTags,
          ...watchListTags,
        ].filter(tag => !aggregatorTags.find(at => at.tagName === tag.tagName)),
      },
      feature,
    );

    return suggestions.filter(tag => !['', 'ALL'].includes(tag.tagName));
  },
);

export const selectWatchlistPositions = createSelector(
  selectWatchlistState,
  watchListSelectors.selectAll,
);

export const selectWatchlistPosition = createSelector(
  [selectWatchlistState, (_, ticker: string) => ticker],
  (state, ticker) => watchListSelectors.selectById(state, ticker),
);

export const selectWatchlistIds = createSelector(
  selectWatchlistState,
  watchListSelectors.selectIds,
);

export const selectWatchlistEntities = createSelector(
  selectWatchlistState,
  watchListSelectors.selectEntities,
);

export const selectWatchlistTotal = createSelector(
  selectWatchlistState,
  watchListSelectors.selectTotal,
);
